import { FileText, ChevronRight } from 'lucide-react';
import type { SourceInfo } from '../../types';

interface SourceCardProps {
  source: SourceInfo;
  index: number;
  onClick?: () => void;
}

/** Extract a display filename from the source path or title. */
function getDisplayName(source: SourceInfo): string {
  // Prefer extracting the actual filename from the storage path
  if (source.path) {
    const filename = source.path.split('/').pop() || source.path;
    if (filename && filename.toLowerCase() !== 'document') {
      return filename;
    }
  }
  // Fall back to title if it's not the generic "Document"/"document"
  if (source.title && source.title.toLowerCase() !== 'document') {
    return source.title;
  }
  // Last resort: return whatever path we have
  return source.path || source.title || 'Unknown';
}

/** Truncate a name to maxLen characters, adding ellipsis if needed. */
function truncateName(name: string, maxLen: number = 30): string {
  if (name.length <= maxLen) return name;
  return name.slice(0, maxLen) + '...';
}

export function SourceCard({ source, index, onClick }: SourceCardProps) {
  const relevancePercentage = Math.round(source.relevance_score * 100);
  const displayName = getDisplayName(source);

  const getRelevanceColor = (score: number) => {
    if (score >= 0.8) return 'text-green-700';
    if (score >= 0.5) return 'text-yellow-700';
    return 'text-gray-500';
  };

  const truncatedName = truncateName(displayName);

  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-1.5 px-1.5 py-1 rounded-md border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-all text-left group"
      aria-label={`Source ${index}: ${displayName}`}
    >
      <span className="flex-shrink-0 w-4 h-4 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-[8px] font-medium">
        {index}
      </span>
      <FileText size={10} className="flex-shrink-0 text-gray-400" />
      <span
        className="flex-1 text-[10px] font-medium text-gray-800 truncate"
        title={displayName}
      >
        {truncatedName}
      </span>
      <span className={`flex-shrink-0 text-[10px] font-medium ${getRelevanceColor(source.relevance_score)}`}>
        {relevancePercentage}%
      </span>
      <ChevronRight
        size={12}
        className="flex-shrink-0 text-gray-400 group-hover:text-primary-500 transition-colors"
      />
    </button>
  );
}
